import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaEye, FaLink, FaPaperPlane } from 'react-icons/fa';
import { TbPlus } from 'react-icons/tb';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import PageHeader from '../../components/misc/PageHeader';
import {
  CreateProductDto,
  ProductDto,
  UpdateProductDto,
} from '../../../services/model/marketplace.model';
import SearchBar from '../../components/forms/SearchBar';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import OrdersModal from '../../components/modals/OrdersModal';
import ProductModal from '../../components/modals/ProductModal';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import ProgressBar from '../../components/misc/ProgressBar';
import Button from '../../components/buttons/Button';
import {
  createProduct,
  deleteProduct,
  getPaymentLink,
  getProducts,
  updateProduct,
} from '../../../services/products.marketplace.service';
import { checkOnboarding } from '../../../services/merchant.marketplace.service';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';

interface MarketplaceProductsPageProps {
  hideHeader?: boolean;
}

export default function MarketplaceProductsPage({
  hideHeader = false,
}: MarketplaceProductsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.marketplaceProducts' });
  const navigate = useNavigate();
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.B2C_CONNECTION,
  );

  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [onboarded, setOnboarded] = useState<boolean | null>(null);

  // Modal states
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productModalMode, setProductModalMode] = useState<'CREATE' | 'EDIT'>('CREATE');
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | undefined>();

  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [ordersProductId, setOrdersProductId] = useState<string>('');
  const [ordersProductName, setOrdersProductName] = useState<string>('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductDto | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const status = await checkOnboarding();
        setOnboarded(status.onboarded);
        if (status.onboarded) {
          await loadProducts();
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
        setOnboarded(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const fetchedProducts = await getProducts();
      setProducts(fetchedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (data: CreateProductDto) => {
    try {
      const newProduct = await createProduct(data);
      setProducts([...products, newProduct]);
      return await Promise.resolve();
    } catch (error: any) {
      console.error('Error creating product:', error);

      // Handle specific error cases
      if (error.response?.data?.code === 'P2002') {
        toast.error(
          'A product already exists for this business. You can only have one product per business.',
        );
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to create product. Please try again.');
      }

      return Promise.reject(error);
    }
  };

  const handleUpdateProduct = async (data: UpdateProductDto) => {
    if (!selectedProduct) return Promise.reject(new Error('No product selected'));

    try {
      const updatedProduct = await updateProduct(selectedProduct.id, data);
      setProducts(products.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)));
      return await Promise.resolve();
    } catch (error) {
      console.error('Error updating product:', error);
      return Promise.reject(error);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await deleteProduct(productToDelete.id);
      setProducts(products.filter((p) => p.id !== productToDelete.id));
      setProductToDelete(null);
      setDeleteModalOpen(false);
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleGetPaymentLink = async (productId: string) => {
    try {
      const link = await getPaymentLink(productId);
      navigator.clipboard.writeText(link.paymentLink);
      toast.success(t('toast.success.copiedToClipboard'));
    } catch (error) {
      console.error('Error getting payment link:', error);
      toast.error(t('toast.error.failedToGetPaymentLink'));
    }
  };

  const openCreateModal = () => {
    setProductModalMode('CREATE');
    setSelectedProduct(undefined);
    setProductModalOpen(true);
  };

  const openEditModal = (product: ProductDto) => {
    setProductModalMode('EDIT');
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const openOrdersModal = (product: ProductDto) => {
    setOrdersProductId(product.id);
    setOrdersProductName(product.name);
    setOrdersModalOpen(true);
  };

  const openDeleteModal = (product: ProductDto) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const navigateToOrders = (productId: string) => {
    navigate(`/financial/orders?productId=${productId}`);
  };

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }

    const query = searchQuery.toLowerCase().trim();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        (product.description && product.description.toLowerCase().includes(query)),
    );
  }, [products, searchQuery]);

  return (
    <>
      {!hideHeader ? (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={openCreateModal}>
              <TbPlus /> {t('newProduct')}
            </Button>
          }
        />
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {products.length > 0 && (
            <>
              <SearchBar
                className="max-w-[380px]"
                search={searchQuery}
                setSearch={setSearchQuery}
              />
              <Button variant="primary-dark" onClick={openCreateModal}>
                <TbPlus /> {t('newProduct')}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-lg text-gray-500">{t('loading')}</div>
          </div>
        ) : onboarded === false ? (
          <EmptyScreenView
            title={t('notOnboarded.title')}
            subtitle={t('notOnboarded.subtitle')}
            primaryButton={
              <Link to="/financial/sales-collect" state={{ activeTab: 'paymentSettings' }}>
                <Button variant="primary-dark">{t('notOnboarded.goToMerchant')}</Button>
              </Link>
            }
          />
        ) : products.length === 0 ? (
          <EmptyScreenView
            title={t('noProducts')}
            subtitle={t('createFirstProduct')}
            primaryButton={
              <Button variant="primary-dark" onClick={openCreateModal}>
                <TbPlus /> {t('newProduct')}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-8">
            <BaseTable>
              <BaseTableHeader>
                <BaseTableHeaderRow>
                  <HeaderCell>{t('name')}</HeaderCell>
                  <HeaderCell>{t('price')}</HeaderCell>
                  <HeaderCell>{t('numberOfRequests')}</HeaderCell>
                  <HeaderCell>{t('progress')}</HeaderCell>
                  <HeaderCell>{t('totalAmountCollected')}</HeaderCell>
                  <HeaderCell>{t('actions')}</HeaderCell>
                </BaseTableHeaderRow>
              </BaseTableHeader>
              <BaseTableBody>
                <BaseTableSpacerRow />
                {filteredProducts.map((product, index) => {
                  const stats = {
                    numberOfRequests: product.numberOfRequests,
                    totalCollected: product.totalRevenueCollected,
                    total: product.totalRevenue,
                    progress:
                      product.totalRevenue > 0
                        ? (product.totalRevenueCollected / product.totalRevenue) * 100
                        : 0,
                  };
                  return (
                    <BaseTableDataRow
                      className="group cursor-pointer transition-all hover:bg-secondary-200"
                      key={`${product.id}_tr_${index}`}>
                      <DataCell onClick={() => navigateToOrders(product.id)}>
                        <div className="flex items-center">
                          {product.imageUrl && (
                            <img
                              className="mr-3 h-10 w-10 rounded-lg object-cover"
                              src={product.imageUrl}
                              alt={product.name}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            {product.description && (
                              <div className="text-sm text-gray-500">{product.description}</div>
                            )}
                          </div>
                        </div>
                      </DataCell>
                      <DataCell onClick={() => navigateToOrders(product.id)}>
                        {formatEuroCents(product.price, i18n.language)}
                      </DataCell>
                      <DataCell onClick={() => navigateToOrders(product.id)}>
                        {stats.numberOfRequests}
                      </DataCell>
                      <DataCell onClick={() => navigateToOrders(product.id)}>
                        <div className="flex flex-col gap-1">
                          <ProgressBar
                            progress={stats.progress}
                            variant="primary-light"
                            showText={false}
                          />
                          <div className="flex justify-between text-xs">
                            <span>{stats.progress.toFixed(0)}%</span>
                            <span>{formatEuroCents(stats.total, i18n.language)}</span>
                          </div>
                        </div>
                      </DataCell>
                      <DataCell onClick={() => navigateToOrders(product.id)}>
                        {formatEuroCents(stats.totalCollected, i18n.language)}
                      </DataCell>
                      <DataCell>
                        <ActionMenu>
                          <ActionMenuButton
                            icon={FaEdit}
                            text={t('edit')}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(product);
                            }}
                          />
                          <ActionMenuButton
                            icon={FaLink}
                            text={t('copyPaymentLink')}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGetPaymentLink(product.id);
                            }}
                          />
                          <ActionMenuButton
                            icon={FaPaperPlane}
                            text={t('viewOrders')}
                            onClick={(e) => {
                              e.stopPropagation();
                              openOrdersModal(product);
                            }}
                          />
                          <ActionMenuButton
                            icon={FaEye}
                            text={t('seeDetails')}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToOrders(product.id);
                            }}
                          />
                          <ActionMenuButton
                            icon={FaTrash}
                            text={t('delete')}
                            className="text-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(product);
                            }}
                          />
                        </ActionMenu>
                      </DataCell>
                    </BaseTableDataRow>
                  );
                })}
              </BaseTableBody>
            </BaseTable>
          </div>
        )}
      </div>

      {/* Product Modal */}
      <ProductModal
        open={productModalOpen}
        setOpen={setProductModalOpen}
        mode={productModalMode}
        product={selectedProduct}
        onSubmit={productModalMode === 'CREATE' ? handleCreateProduct : handleUpdateProduct}
      />

      {/* Orders Modal */}
      <OrdersModal
        open={ordersModalOpen}
        setOpen={setOrdersModalOpen}
        productId={ordersProductId}
        productName={ordersProductName}
        members={connections}
      />

      {/* Delete Confirmation Modal */}
      <AreYouSureModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete Product"
        description={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteProduct}
        saveClassName="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}
